import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { Link, useNavigate } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Building2, Users, Activity, ChevronRight, 
  AlertTriangle, CheckCircle, RefreshCw, 
  TrendingUp, Zap
} from 'lucide-react';

export default function SuperAdminDashboard() {
  const { user, API } = useContext(AppContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    tenants: [],
    totalUsers: 0,
    settings: null
  });

  useEffect(() => {
    if (!user?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadDashboardData();
  }, [user, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [tenantsRes, usersRes, settingsRes] = await Promise.all([
        authAxios.get(`${API}/platform/tenants`),
        authAxios.get(`${API}/platform/users`),
        authAxios.get(`${API}/platform/settings`)
      ]);
      
      setStats({
        tenants: tenantsRes.data,
        totalUsers: usersRes.data.length,
        settings: settingsRes.data
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.is_super_admin) {
    return null;
  }

  const activeTenants = stats.tenants.filter(t => t.status === 'active').length;
  const suspendedTenants = stats.tenants.filter(t => t.status === 'suspended').length;

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Dashboard</h1>
            <p className="text-sm text-zinc-500">Platform overview and quick stats</p>
          </div>
          <Button 
            onClick={loadDashboardData} 
            variant="outline" 
            size="sm" 
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Leagues */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Leagues</p>
                  <p className="text-3xl font-bold text-zinc-100 mt-1">{stats.tenants.length}</p>
                </div>
                <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-amber-500" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-zinc-500">
                <TrendingUp className="w-3 h-3 text-emerald-500" />
                <span className="text-emerald-500">{activeTenants} active</span>
              </div>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Users</p>
                  <p className="text-3xl font-bold text-zinc-100 mt-1">{stats.totalUsers}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-zinc-500">
                <Activity className="w-3 h-3" />
                <span>Across all leagues</span>
              </div>
            </CardContent>
          </Card>

          {/* Active Leagues */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Active</p>
                  <p className="text-3xl font-bold text-emerald-500 mt-1">{activeTenants}</p>
                </div>
                <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-zinc-500">
                <Zap className="w-3 h-3 text-amber-500" />
                <span>Running leagues</span>
              </div>
            </CardContent>
          </Card>

          {/* Suspended */}
          <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Suspended</p>
                  <p className={`text-3xl font-bold mt-1 ${suspendedTenants > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                    {suspendedTenants}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  suspendedTenants > 0 ? 'bg-red-500/10' : 'bg-zinc-800'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${suspendedTenants > 0 ? 'text-red-400' : 'text-zinc-600'}`} />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1 text-xs text-zinc-500">
                <span>{suspendedTenants > 0 ? 'Needs attention' : 'All clear'}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform Status */}
        {stats.settings && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-zinc-100">Platform Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge 
                  className={`text-xs ${stats.settings.allow_new_registrations 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}
                >
                  Registrations: {stats.settings.allow_new_registrations ? 'Open' : 'Closed'}
                </Badge>
                <Badge 
                  className={`text-xs ${stats.settings.allow_new_leagues 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}
                >
                  New Leagues: {stats.settings.allow_new_leagues ? 'Allowed' : 'Blocked'}
                </Badge>
                <Badge 
                  className={`text-xs ${stats.settings.maintenance_mode 
                    ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' 
                    : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                  }`}
                >
                  Maintenance: {stats.settings.maintenance_mode ? 'ON' : 'OFF'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Leagues */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-zinc-100">Recent Leagues</CardTitle>
              <Link to="/super-admin/tenants">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                  data-testid="view-all-tenants-btn"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-500">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : stats.tenants.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No leagues yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.tenants.slice(0, 5).map(tenant => (
                  <Link 
                    key={tenant.id} 
                    to={`/super-admin/tenants/${tenant.id}`}
                    className="block"
                    data-testid={`tenant-${tenant.id}`}
                  >
                    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 transition-colors">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 bg-zinc-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-zinc-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-zinc-100 text-sm truncate">{tenant.name}</p>
                          <p className="text-xs text-zinc-500 truncate">{tenant.owner?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                        <Badge 
                          className={`text-[10px] ${tenant.status === 'active' 
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}
                        >
                          {tenant.status}
                        </Badge>
                        <span className="text-xs text-zinc-500 hidden sm:block">{tenant.member_count} members</span>
                        <ChevronRight className="w-4 h-4 text-zinc-600" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
}
